/**
 * MONSTER TOWN — THE KID'S MONSTER. OWNED BY THE MONSTER CREW (src/monster/**).
 *
 * This character is the ownership anchor: it is *them* on screen. It walks with
 * personality, blinks, breathes, fidgets when left alone, and reacts to every
 * single thing they tap.
 *
 * CONTRACTS KEPT FROM THE SCAFFOLD (other crews compile against these):
 *   - `play:walkTo` / `play:interactRequested` / `play:interacted`
 *   - `app.playerPos` written every fixed step (camera and NPCs read it)
 *   - the `monster` save section id
 *   - `installPlayer(ctx)`, `playerPosition()`, `teleportPlayer(p)` exports
 *
 * ADDED HERE: real rigged art, the animator, `monster:action` playback, facing,
 * obstacle-sliding walk, the first-launch monster creator hand-off, and the
 * carry slot the venue crew needs for "walk a plate to a table".
 */

import type * as THREE from 'three'
// Values, not types: the dev-only framing probe below measures a real bounding
// box. Both are already in the bundle a dozen times over.
import { Box3, Vector3 } from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { rotYaw, type Cell, type Grid, type Placement } from '../core/grid'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import { requirePlaceable } from '../core/placeable'
import { MONSTER_BODY_COLORS, type PaletteName } from '../core/palette'
import {
  DEFAULT_MONSTER,
  type InstanceId,
  type MonsterBodyShape,
  type MonsterConfig,
  type MonsterEyes,
  type MonsterHorns,
  type MonsterPattern,
  type Vec3,
} from '../core/types'
import { ZOOM_STEPS } from '../render/camera'
import { app, type GameContext } from '../app/context'
import {
  buildMonster,
  currentViewYaw,
  facingBiasedToCamera,
  safePalette,
  setViewYaw,
  type MonsterRig,
} from './art'
import { MonsterAnimator } from './anim'
import { nearestWalkable, standoffPoint, Walker } from './walker'
import { installCreator, maybeOpenOnFirstLaunch } from './creator'
import { aimStage, CAMERA_PITCH, cameraYaw } from './stage'
import { HAT_IDS } from './wearables'
import './events'

/** How close the monster stands to a thing before it interacts with it. */
const INTERACT_DIST = 1.5
const WALK_SPEED = 4.6

/**
 * SCALE. The rig is authored at interior scale (a 1-unit tile, a 1.4-unit
 * ceiling) and scaled UP outdoors, where one tile is 2 units and a
 * doll-house-sized monster would be a thumbprint on the plot. The creator
 * scales it up again — on that screen the monster IS the content.
 */
const EXTERIOR_SCALE = 1.5
const INTERIOR_SCALE = 1
const CREATOR_SCALE = 3.2

/**
 * The size the creator STAGE is built and framed against — a fixed reference
 * rather than the live rig height, so swapping between a squat blob and a tall
 * monster does not resize the plinth and re-frame the camera under the kid's
 * finger. The tallest shape is 1.49 rig units, the shortest 1.0.
 */
export const CREATOR_STAGE_HEIGHT = 1.42 * CREATOR_SCALE

/**
 * The box the creator CAMERA frames — a hair over the tallest body shape
 * (`tall` is 1.49 rig units), not the average.
 *
 * Framing against the average is why picking a tall monster used to push its
 * horns off the top of the frame while the dock ate its feet. Framing against
 * the tallest means the zoom never changes when the kid tries the four shapes
 * one after another, which is exactly what they do on this screen: the monster
 * grows and shrinks in place instead of the camera lurching.
 */
export const CREATOR_FRAME_HEIGHT = 1.55 * CREATOR_SCALE

interface MonsterSave {
  look: MonsterConfig
  x: number
  z: number
  /** False until the kid has been through the creator once. */
  created: boolean
  /** Hats/outfits the kid owns. The shop crew grants these; we wear them. */
  wardrobe: string[]
}

/** Day-one wearables. Everything else arrives through 'monster:grantWearable'. */
const STARTER_WARDROBE: readonly string[] = ['cap', 'bow']

let rig: MonsterRig | null = null
let animator: MonsterAnimator | null = null
let created = false
let owned: string[] = [...STARTER_WARDROBE]
let builtScale = 0
const walker = new Walker({ maxSpeed: WALK_SPEED, arriveDist: 0.32 })
let pendingInteract: InstanceId | null = null
let ctxRef: GameContext | null = null
let stepPhase = 0
/** Non-null while the creator owns the monster: quarter turns off camera-facing. */
let spinOffset: number | null = null
let stageRot = -1
/**
 * The yaw actually drawn, eased toward the yaw the walker wants. Separate from
 * `walker.facing` so an idle monster can turn to the lens without the walker
 * thinking it has been steered.
 */
let shownFacing = 0
/**
 * How far off its own heading an IDLE monster may be turned toward the camera.
 * 1.15 rad (66 degrees) is a three-quarter view; the rig's face turn covers the
 * rest. Matches `IDLE_FACE_LIMIT` in `npc/npc.ts` on purpose — the kid's monster
 * and the neighbours must behave the same or the difference reads as a bug.
 */
const IDLE_FACE_LIMIT = 1.15

// ---------------------------------------------------------------------------
// hydration helpers — a corrupt save must never take the monster down
// ---------------------------------------------------------------------------

const SHAPES: readonly MonsterBodyShape[] = ['blob', 'tall', 'round', 'chunky']
const EYES: readonly MonsterEyes[] = ['two', 'one', 'three', 'sleepy']
const HORNS: readonly MonsterHorns[] = ['none', 'nubs', 'curved', 'antlers', 'antennae']
const PATTERNS: readonly MonsterPattern[] = ['plain', 'spots', 'stripes', 'belly', 'star']

function pick<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
}

function safeConfig(raw: unknown): MonsterConfig {
  const d = DEFAULT_MONSTER
  if (typeof raw !== 'object' || raw === null) return { ...d }
  const r = raw as Record<string, unknown>
  const color = pick(r['color'], MONSTER_BODY_COLORS as readonly PaletteName[], d.color)
  return {
    shape: pick(r['shape'], SHAPES, d.shape),
    color,
    accentColor: safePalette(r['accentColor'], d.accentColor),
    eyes: pick(r['eyes'], EYES, d.eyes),
    horns: pick(r['horns'], HORNS, d.horns),
    pattern: pick(r['pattern'], PATTERNS, d.pattern),
    hat: typeof r['hat'] === 'string' ? r['hat'] : null,
    outfit: typeof r['outfit'] === 'string' ? r['outfit'] : null,
  }
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installPlayer(ctx: GameContext): void {
  ctxRef = ctx

  registerSaveSection<MonsterSave>(
    {
      id: 'monster',
      version: 2,
      defaults: () => ({
        look: { ...DEFAULT_MONSTER },
        x: 0,
        z: 0,
        created: false,
        wardrobe: [...STARTER_WARDROBE],
      }),
      serialize: () => ({ look: app.monster, x: walker.x, z: walker.z, created, wardrobe: owned }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const saved = c.arr<string>(raw['wardrobe'], (e) =>
          typeof e === 'string' && HAT_IDS.includes(e) ? e : null,
        )
        return {
          look: safeConfig(raw['look']),
          x: c.num(raw['x'], c.defaults.x),
          z: c.num(raw['z'], c.defaults.z),
          // A v1 save predates the creator; treat those monsters as made.
          created: c.savedVersion < 2 ? true : c.bool(raw['created'], false),
          // The starter wearables can never be lost, however odd the save is.
          wardrobe: [...new Set([...STARTER_WARDROBE, ...saved])],
        }
      },
    },
    (value) => {
      app.monster = value.look
      created = value.created
      owned = [...value.wardrobe]
      walker.setPosition(value.x, value.z)
      rebuild()
    },
  )

  installCreator(ctx)
  rebuild()

  // The shop/economy crew hands over a hat; we own putting it on.
  bus.on('monster:grantWearable', (e) => {
    if (owned.includes(e.id)) return
    owned = [...owned, e.id]
    bus.emit('monster:wearableGranted', { id: e.id, wardrobe: [...owned] })
    save.request('monster:wearable')
  })

  // First launch: make your monster before you go and live in the town.
  // Deferred one tick because `save.load()` runs mid-boot and `main.ts` sets the
  // phase to 'town' immediately afterwards — opening now would be closed again.
  bus.on('save:loaded', () => {
    setTimeout(() => maybeOpenOnFirstLaunch(), 0)
  })

  bus.on('play:walkTo', (e) => {
    if (e.sceneId !== world.activeSceneId) return
    pendingInteract = null
    const node = world.get(e.sceneId)
    const spot = node === undefined ? e.target : nearestWalkable(node.grid, e.target.x, e.target.z)
    walker.walkTo(spot.x, spot.z, 0.3)
    animator?.stop()
  })

  bus.on('play:interactRequested', (e) => {
    const node = world.get(e.sceneId)
    if (node === undefined) return
    const p = node.grid.placement(e.instanceId)
    if (p === null) return
    const centre = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
    /**
     * STAND CLOSE ENOUGH THAT THE REACTION READS AS BELONGING TO THE THING.
     *
     * `INTERACT_DIST` is 1.5 WORLD units and was applied raw in every scene.
     * Outdoors, where a tile is 2 units, that is three quarters of a tile —
     * right next to the postbox, which is where it was tuned. Indoors a tile is
     * 1 unit, so the same constant parked the monster a tile and a half back,
     * and the telly's footprint term pushed it to 2.1 units — TWO WHOLE TILES
     * from a set it had supposedly walked over to watch.
     *
     * A kid taps the telly, the monster stops in the middle of the room and
     * something twitches on the far side of the floor. The tap and the reaction
     * never join up, which is exactly how "nothing happened" is read at three.
     * Captured before the fix at `shots/fix-tv-diag/t30.png`.
     *
     * Scaling by the grid keeps every outdoor standoff bit-identical
     * (exterior tileSize is 2, so the factor is 1) and halves the indoor one.
     */
    const reach =
      INTERACT_DIST * (node.grid.tileSize / 2) +
      Math.max(p.footprint.w, p.footprint.d) * node.grid.tileSize * 0.3
    const here = { x: walker.x, y: 0, z: walker.z }
    /**
     * WALK ROUND TO THE FRONT OF A THING THAT HAS A FRONT.
     *
     * `standoffPoint` stops on whichever side the monster happened to be
     * approaching from, which is fine for a rug and wrong for anything a
     * monster is meant to be looking AT. Once the standoff was tightened above,
     * tapping the telly parked the monster BEHIND the set, where it stood in
     * the way of the screen it had just switched on — the fix for one defect
     * quietly creating another. Before/after: `shots/fix-tv-diag/t30.png`
     * and `shots/fix-tv-iso/i5-close-watching.png`.
     *
     * Every `buildArt` is authored facing +Z and rotated by the placement's
     * `rot` (the grid contract), so the front is known for free. Take it when
     * the monster can actually stand there, and when it is not a silly detour —
     * a kid should never watch their monster hike around the room because a
     * chair happened to be pointing the other way.
     */
    const yaw = rotYaw(p.rot)
    const front = {
      x: centre.x + Math.sin(yaw) * reach,
      y: 0,
      z: centre.z + Math.cos(yaw) * reach,
    }
    const side = standoffPoint(here, centre, reach)
    const detour = Math.hypot(front.x - here.x, front.z - here.z)
    const direct = Math.hypot(side.x - here.x, side.z - here.z)
    const preferFront =
      node.grid.isWorldWalkable(front.x, front.z) &&
      detour - direct < node.grid.tileSize * 3
    const raw = preferFront ? front : side
    /**
     * SNAP TO SOMEWHERE THE MONSTER CAN ACTUALLY STAND.
     *
     * `play:walkTo` right above has always run its target through
     * `nearestWalkable`; this path did not, and walked to the raw standoff point
     * instead. When that point fell inside a wall, a neighbouring shelf, or
     * outside the room — which is precisely what happens to anything in a
     * corner or backed against a wall, i.e. where furniture actually goes — the
     * monster set off, never arrived, and `play:interacted` never fired. The tap
     * was accepted and then quietly dropped, so the prop looked dead.
     *
     * That is LAW #1 broken by geometry rather than by a missing handler, which
     * is why the placeable audit could not see it: three of the town shop's
     * shelves were unbuyable, and any prop a kid tucks into a corner would go
     * the same way. Both paths now use the same rule.
     */
    /**
     * ...AND SOMEWHERE THAT IS ACTUALLY NEXT TO THE THING (venue fix, r3).
     *
     * `nearestWalkable` searches outward in blind rings from wherever the raw
     * standoff point happens to land, and it takes the first walkable cell in
     * scan order — not the nearest one to the walker, and with no idea which
     * object we were trying to reach. In the ice cream shop, tapping the cone
     * stack from the middle of the floor put the raw point inside the serving
     * counter, and the ring search handed back a cell TWO TILES the wrong side
     * of that counter. The monster then had to walk through a solid counter to
     * get there, which it cannot, so the tap died.
     *
     * Walking to a tile that touches the thing you tapped is both what a person
     * would do and the only target that is reliably reachable, so it is tried
     * first: the free tiles around the footprint, nearest to the monster wins.
     */
    const spot = standingSpotFor(node.grid, p, raw)
    pendingInteract = e.instanceId
    walker.walkTo(spot.x, spot.z, 0.3)
    animator?.stop()
  })

  // LAW #1's payoff: a prop asked the monster to do something, so it does it.
  bus.on('monster:action', (e) => {
    animator?.play(e.action, e.seconds)
  })

  bus.on('scene:entered', () => rebuild())

  /**
   * ARRIVE IN THE ROOM YOU WALKED INTO.
   *
   * Every interior has its own grid origin — the ice cream shop's starts at
   * world (-19.5, 23.5) — but the player kept whatever world position it had in
   * the scene it just left. `transitionTo` has always taken a `spawnCell` and
   * `usePortalAt` has always passed the portal's `toCell`; nothing consumed it.
   * So a kid tapped their shop's front door, the room loaded, and the camera
   * (which follows the monster) stayed pointed at empty sky some 20 metres away,
   * with every counter and tub off-screen and untappable. The house was the only
   * room that worked, and only by the accident of sitting near the origin.
   *
   * Two rules, in order:
   *   1. If the portal said where to stand, stand there.
   *   2. Otherwise, only move if the player is not actually IN this scene's grid
   *      — so a harness jump, a re-entry, or any future scene switch still lands
   *      the monster somewhere real instead of stranding it outside the room.
   * Either way the landing spot is snapped to something walkable, so a kid never
   * spawns inside the furniture.
   */
  bus.on('scene:spawnAt', (e) => {
    const node = world.get(e.scene)
    if (node === undefined) return
    const grid = node.grid
    let want: Vec3 | null = null

    if (e.spawn !== null) {
      want = grid.cellToWorld({ ...e.spawn, gy: 0 })
    } else {
      const here = grid.worldToCell(walker.x, walker.z, 0)
      if (!grid.inBounds(here)) {
        want = grid.cellToWorld({
          gx: Math.floor(grid.spec.width / 2),
          gy: 0,
          gz: Math.floor(grid.spec.depth / 2),
        })
      }
    }
    if (want === null) return

    const spot = nearestWalkable(grid, want.x, want.z)
    teleportPlayer({ x: spot.x, y: 0, z: spot.z })
    ctxRef?.camera.lookAtPoint({ x: spot.x, y: 0.4, z: spot.z }, true)
    frameSceneZoom(node)
  })

  // Entering / leaving the creator changes how big the monster is drawn.
  bus.on('app:phaseChanged', () => {
    if (Math.abs(builtScale - monsterScale()) > 0.001) rebuild()
  })

  // The build drawer says how much of the canvas it is covering; the framing
  // rises by half of it so the piece being placed is never behind it.
  bus.on('build:reserve', (e) => {
    buildReserve = Math.max(0, Math.min(0.6, e.fraction))
  })

  installPlayerDebug()

  engine.addSystem({
    id: 'monster.player',
    order: 30,
    fixed: (dt) => {
      const node = world.activeSceneId === null ? undefined : world.get(world.activeSceneId)
      const arrived = walker.step(dt, node?.grid ?? null)
      app.playerPos = { x: walker.x, y: 0, z: walker.z }
      footsteps(dt)
      unstick(node?.grid ?? null)
      if (arrived) finishArrival()
    },
    render: (_alpha, frameMs) => {
      if (ctxRef !== null) {
        /**
         * PUBLISH WHERE THE CAMERA IS, ONCE, BEFORE ANY MONSTER IS POSED.
         *
         * This system is engine order 30; neighbours are 40-49 and the venue is
         * 50-59, so every monster in the frame turns its face against the same
         * yaw on the same frame. Set before the `rig` guard on purpose — the
         * neighbours must still get a correct view direction on any frame the
         * kid's own monster happens not to be built.
         */
        setViewYaw(cameraYaw(ctxRef.camera.rotation))
      }
      if (rig === null || animator === null || ctxRef === null) return
      rig.root.position.set(walker.x, 0, walker.z)
      /**
       * ON THE CREATOR PLINTH THE MONSTER LOOKS AT THE CAMERA.
       *
       * Facing +Z under a 45-degree iso camera showed the face three-quarters
       * on, which is why the eyes read as different sizes at different heights
       * and the blush on the far cheek fell off the silhouette: the face was
       * symmetrical in world space and asymmetrical on screen. Turned to the
       * lens, left and right land at the same screen height. Recomputed every
       * frame so it stays true through a camera rotation from anywhere.
       */
      if (spinOffset === null) {
        /**
         * WALKING, IT FACES WHERE IT IS GOING. STANDING, IT LOOKS UP AT THE KID.
         *
         * The monster kept whatever yaw it stopped on, and the camera has four
         * of them: tap the rotate button twice and the kid is looking at the
         * back of their own monster's head with one eye on the limb. Captured at
         * `shots/fix-monster-r2-verify/kitchen-rot2.png` — 78 degrees of residual
         * on a monster that was doing nothing at all.
         *
         * `facingBiasedToCamera` pulls an IDLE monster no further than
         * `IDLE_FACE_LIMIT` off its own heading, and the rig's face turn takes
         * the remaining ~45 degrees, so a standing monster always has both eyes
         * on the kid without ever snapping square-on like a cut-out. The same
         * treatment the neighbours get, for the same reason.
         */
        const want = walker.walking
          ? walker.facing
          : facingBiasedToCamera(walker.facing, IDLE_FACE_LIMIT)
        let turn = want - shownFacing
        while (turn > Math.PI) turn -= Math.PI * 2
        while (turn < -Math.PI) turn += Math.PI * 2
        shownFacing += turn * Math.min(1, (frameMs / 1000) * 7)
        rig.root.rotation.y = shownFacing
      } else {
        const rot = ctxRef.camera.rotation
        rig.root.rotation.y = cameraYaw(rot) + spinOffset
        if (rot !== stageRot) {
          stageRot = rot
          aimStage(rot, CREATOR_STAGE_HEIGHT)
        }
      }
      animator.setSpeed(walker.gait)
      animator.update(frameMs / 1000)
      ctxRef.camera.lookAtPoint(cameraTarget())
      ctxRef.renderer.followSun(walker.x, walker.z)
    },
  })
}

/**
 * DEV-ONLY WALK PROBE. Stripped from the production bundle by Vite.
 *
 * Exists because "the monster did not walk to the thing I tapped" is invisible
 * from outside: `app.playerPos` only tells you where it ended up, never what it
 * was aiming at or whether the floor under it was considered walkable at all.
 * `tools/fix-r1-probe.mjs` and the interaction drive step read this.
 */
function installPlayerDebug(): void {
  if (!import.meta.env.DEV) return
  ;(window as unknown as Record<string, unknown>)['__mtWalk'] = {
    state: () => ({
      x: walker.x,
      z: walker.z,
      tx: walker.target.x,
      tz: walker.target.z,
      walking: walker.walking,
      speed: walker.speed,
      arriveDist: walker.arriveDist,
      pending: pendingInteract,
      /**
       * WHAT THE MONSTER IS ACTUALLY DOING. Added by the integration fix crew:
       * `monster:action` firing on the bus proved nothing, because the animator
       * could — and did — cancel the action on the same frame it started. The
       * only way to see that from a tool is to ask the animator.
       */
      action: animator?.currentAction ?? null,
      actionBusy: animator?.busy ?? false,
    }),
    /** Drop the monster at a world point — including one it should not fit in. */
    put: (x: number, z: number) => {
      teleportPlayer({ x, y: 0, z })
    },
    walkable: (x: number, z: number) => {
      const id = world.activeSceneId
      if (id === null) return false
      return world.require(id).grid.isWorldWalkable(x, z)
    },
    /**
     * WHERE THE MONSTER ACTUALLY LANDS ON THE GLASS, in CANVAS pixels.
     *
     * The creator framing cannot be checked by arithmetic — the fit ladder, the
     * aspect ratio, the pitch and the look-at shove all compound — and it cannot
     * be checked by eye either, because "is the bottom of the monster behind the
     * option dock" is a question about two rectangles. So the tool asks for both
     * rectangles. Canvas rect, never `window.inner*` (the hard rule).
     */
    screenBox: () => {
      if (rig === null || ctxRef === null) return null
      const canvas = ctxRef.canvas
      const cam = ctxRef.camera.camera
      const b = new Box3().setFromObject(rig.root)
      const r = canvas.getBoundingClientRect()
      const v = new Vector3()
      let x0 = Infinity
      let y0 = Infinity
      let x1 = -Infinity
      let y1 = -Infinity
      for (let i = 0; i < 8; i++) {
        v.set(i & 1 ? b.max.x : b.min.x, i & 2 ? b.max.y : b.min.y, i & 4 ? b.max.z : b.min.z)
        v.project(cam)
        const px = ((v.x + 1) / 2) * r.width
        const py = ((1 - v.y) / 2) * r.height
        x0 = Math.min(x0, px)
        y0 = Math.min(y0, py)
        x1 = Math.max(x1, px)
        y1 = Math.max(y1, py)
      }
      const dock = document.querySelector('#mt-creator .mt-cr-island')?.getBoundingClientRect() ?? null
      return {
        canvas: { w: Math.round(r.width), h: Math.round(r.height) },
        monster: { x0: Math.round(x0), y0: Math.round(y0), x1: Math.round(x1), y1: Math.round(y1) },
        dockTop: dock === null ? null : Math.round(dock.top - r.top),
        cam: ctxRef.camera.debugState(),
      }
    },
    /**
     * EVERY FACE IN THE LIVE SCENE, and how far off the lens it is.
     *
     * "Is an eye riding the silhouette edge" is an angle in the rig, not
     * something to squint at in a PNG: past roughly 76 degrees of residual the
     * outer eye starts to cross the limb. This reports the residual for the
     * kid's monster, every neighbour and every customer at once, so the fix is
     * checked on the whole cast rather than on whichever one is in frame.
     */
    faces: () => {
      const id = world.activeSceneId
      if (id === null || ctxRef === null) return null
      const node = world.get(id)
      if (node === undefined) return null
      const yaw = currentViewYaw()
      const list: Record<string, unknown>[] = []
      node.actors.traverse((o) => {
        if (o.name !== 'monster') return
        const face = o.getObjectByName('face')
        if (face === undefined) {
          list.push({ face: 'MISSING' })
          return
        }
        face.updateWorldMatrix(true, false)
        const m = face.matrixWorld.elements
        const faceYaw = Math.atan2(m[8] ?? 0, m[10] ?? 1)
        let d = yaw - faceYaw
        while (d > Math.PI) d -= Math.PI * 2
        while (d < -Math.PI) d += Math.PI * 2
        let meshes = 0
        o.traverse((c) => {
          if ((c as THREE.Mesh).isMesh === true) meshes++
        })
        list.push({
          // Every part is its own mesh, so this IS the monster's draw calls.
          meshes,
          visible: face.visible,
          residualDeg: Math.round((d * 180) / Math.PI),
          eyes: face.children.filter((c) => c.name.startsWith('eye')).length,
          mouth: face.children.some((c) => c.name === 'mouth'),
          blush: face.children.filter((c) => c.name === '' || c.name === 'mesh').length,
          parts: face.children.length,
        })
      })
      return { viewYawDeg: Math.round((yaw * 180) / Math.PI), scene: id, monsters: list }
    },
    /** The 5x5 walkable neighbourhood around the monster, as rows of 0/1. */
    around: (r = 2) => {
      const id = world.activeSceneId
      if (id === null) return []
      const grid = world.require(id).grid
      const c = grid.worldToCell(walker.x, walker.z, 0)
      const rows: string[] = []
      for (let dz = -r; dz <= r; dz++) {
        let row = ''
        for (let dx = -r; dx <= r; dx++) row += grid.isWalkable(c.gx + dx, c.gz + dz) ? '.' : '#'
        rows.push(row)
      }
      return rows
    },
  }
}

/** The kid's own zoom out in the town, so walking indoors never loses it. */
let exteriorZoom: number | null = null

/**
 * PICK A ZOOM THAT FITS THE ROOM YOU JUST WALKED INTO.
 *
 * Zoom was global and survived every door. The town is comfortable at the widest
 * step (24 world units of half-height); a 9x7 living room at that step is a
 * postage stamp floating in the sky with about 30 px of shop plinth to aim at.
 * That is unplayable twice over — you cannot see what you own, and `pickInstance`
 * is deliberately forgiving to 44 px, so at that scale every tap in the room
 * resolves to the same neighbour and half the props look like they do nothing.
 *
 * So: interiors frame themselves, exteriors keep whatever the kid chose. It is
 * still the same fixed iso camera with the same four steps — no new control, no
 * free zoom, nothing for a kid to get wrong.
 */
function frameSceneZoom(node: { def: { kind: string }; grid: { spec: { width: number; depth: number } } }): void {
  const cam = ctxRef?.camera
  if (cam === undefined) return

  if (node.def.kind !== 'interior') {
    // Back outside: give the kid their own view back.
    if (exteriorZoom !== null) {
      cam.setZoomIndex(exteriorZoom, true)
      exteriorZoom = null
    }
    return
  }

  if (exteriorZoom === null) exteriorZoom = cam.zoomLevel

  /**
   * ============ DO NOT MEASURE IN FEET WHEN THE RULER IS IN METRES (fixed r3) ============
   * Round-3 critic, major: "Interior build camera is zoomed so far in that most
   * of the room is off-frame … the left wall, right wall and most of the floor
   * are cut off; the kid can see maybe 40% of the room they are decorating."
   *
   * This function was the cause. The interiors crew fits a room with
   * `camera.setFitBounds()`, which REPLACES the fixed `ZOOM_STEPS` ladder with
   * four steps derived from that room and the live aspect ratio — index 2 is the
   * exact fit, index 0 is 0.6x of it. This code then reasoned about the room's
   * size in raw world units against the raw `ZOOM_STEPS` numbers, found a 9x7
   * room "needs" about 8.5 units against a smallest step of 14, and asked for
   * index 0 — which under the fitted ladder is not "the smallest step that
   * fits", it is 0.6x the fit. The room came out about 1.7x too big for the
   * screen, every time, in every room.
   *
   * When the scene has fitted itself, the fit IS the answer: index 2. The
   * hand-tuned search below is for the fixed ladder, which is the only place its
   * numbers mean anything.
   * =====================================================================================
   */
  if (cam.fitBounds !== null) {
    cam.setZoomIndex(FITTED_ROOM_ZOOM, true)
    return
  }

  // Under a 34-degree iso the on-screen height of a W x D floor is about
  // (W + D) * 0.395 world units. Add a third again for the walls and the HUD.
  const { width, depth } = node.grid.spec
  const needed = (width + depth) * 0.395 * 1.35
  let index = ZOOM_STEPS.length - 1
  for (let i = 0; i < ZOOM_STEPS.length; i++) {
    if ((ZOOM_STEPS[i] ?? 0) * 2 >= needed) {
      index = i
      break
    }
  }
  cam.setZoomIndex(index, true)
}

/** Index 2 of a fitted ladder is "the whole room, filling the frame". */
const FITTED_ROOM_ZOOM = 2

/**
 * Where the camera looks. In the creator the monster is pushed up-screen so the
 * choice buttons underneath never cover the thing the kid is changing — the
 * named critic defect "camera that hides what you're placing", applied to the
 * one screen where the monster IS the thing being placed.
 */
function cameraTarget(): Vec3 {
  if (app.phase !== 'monsterCreator' || ctxRef === null) {
    /**
     * ============ THE SAME SHOVE, FOR THE BUILD DRAWER (fixed r3) ============
     * Round-3 critic, major: "the salmon couch ghost sits at y~830-960 with the
     * build drawer starting at y~985 — the preview is directly against the
     * drawer edge. This is the 'camera hides what is being placed' failure named
     * in the brief."
     *
     * The creator already solved exactly this problem for its own dock, in
     * screen space, with no magic numbers (see the note below). Build mode gets
     * the identical treatment: the drawer reports the fraction of the CANVAS it
     * covers on `build:reserve`, and the framing rises by half of that band, so
     * the monster — and the tile it is about to build on — sit in the middle of
     * what a kid can actually see. Collapse the drawer and the frame settles
     * back on its own, because the number came from the drawer's real rect.
     * =========================================================================
     */
    const cam = ctxRef?.camera
    if (buildReserve > 0 && cam !== undefined) {
      const liftB = (buildReserve * cam.debugState().halfHeight) / Math.cos(CAMERA_PITCH)
      return { x: walker.x, y: 0.4 - liftB, z: walker.z }
    }
    return { x: walker.x, y: 0.4, z: walker.z }
  }
  /**
   * THE SHOVE UP-SCREEN IS MEASURED, NOT GUESSED.
   *
   * It used to be a fixed nudge along the view axis — a quarter of the stage
   * height — which is a number that means nothing in screen space: it moved the
   * monster up by however much the current zoom happened to make it worth. The
   * result is the round-2 defect: the hands cut by the dock and everything below
   * the chest occluded while the kid is choosing a BODY SHAPE.
   *
   * Now: the dock reports what fraction of the canvas it covers, the stage zooms
   * out to fit the monster into what is left, and this centres it in that band.
   * Lowering the look-at by `d` raises everything else on screen by
   * `d * cos(pitch)` world units, and half the screen is `halfHeight` of those —
   * so shifting the frame up by half the reserved band is exactly
   * `reserve * halfHeight / cos(pitch)` of world Y. No magic number: change the
   * dock's height and the framing follows it.
   */
  const half = ctxRef.camera.debugState().halfHeight
  const lift = (creatorReserve * half) / Math.cos(CAMERA_PITCH)
  return { x: walker.x, y: CREATOR_FRAME_HEIGHT * 0.5 - lift, z: walker.z }
}

/**
 * Hand the monster's facing to the creator (a quarter-turn offset from
 * camera-facing), or `null` to give it back to the walker.
 */
export function setPlayerSpin(spin: number | null): void {
  // Hand the eased facing back where the creator left it, or the monster spins
  // a half turn on its own the moment the tick is tapped.
  if (spin === null && spinOffset !== null && rig !== null) shownFacing = rig.root.rotation.y
  spinOffset = spin
  stageRot = -1
}

/**
 * How much of the canvas the creator's option dock covers, 0..0.6. Written by
 * `creator.ts` from the real island rect on open and on every resize, so an iPad
 * turned to portrait re-frames instead of burying the monster.
 */
let creatorReserve = 0

export function setCreatorReserve(fraction: number): void {
  creatorReserve = Math.max(0, Math.min(0.6, fraction))
}

/**
 * The same number for the BUILD DRAWER, published by the build crew on
 * `build:reserve` from the drawer's own rect against the canvas rect. Zero
 * whenever the drawer is not on screen, so play mode is framed exactly as it
 * always was.
 */
let buildReserve = 0

/**
 * WALK OUT OF THE FURNITURE.
 *
 * The walker can now escape a blocked cell (see the escape hatch in
 * `walker.ts`), but nothing ever told it to. A kid who drops a table on the
 * square their monster is standing on — which build mode invites, and which is
 * how the finished demo room lays out around the player — leaves it standing
 * inside the table until they happen to tap somewhere else.
 *
 * So the monster notices on its own and steps out, visibly, the way a person
 * would. Checked six times a second rather than every frame: this walks the
 * grid, and the hand-me-down iPad does not need it 30 times a second to look
 * immediate.
 */
let unstickTick = 0

function unstick(grid: Grid | null): void {
  unstickTick++
  if (unstickTick % 5 !== 0) return
  if (grid === null || walker.walking) return
  if (grid.isWorldWalkable(walker.x, walker.z)) return
  const spot = nearestWalkable(grid, walker.x, walker.z)
  if (Math.hypot(spot.x - walker.x, spot.z - walker.z) < 0.05) return
  walker.walkTo(spot.x, spot.z, 0.12)
  bus.emit('fx:play', { effect: 'dustPuff', at: { x: walker.x, y: 0.1, z: walker.z }, count: 4 })
}

function footsteps(dt: number): void {
  if (walker.gait < 0.2) {
    stepPhase = 0
    return
  }
  stepPhase += dt * (6.6 + walker.gait * 2.2) * walker.gait
  if (stepPhase >= Math.PI) {
    stepPhase -= Math.PI
    bus.emit('audio:cue', { cue: 'step_soft', volume: 0.5 })
  }
}

/**
 * A free tile TOUCHING the thing that was tapped, nearest to where the monster
 * is standing — or the plain nearest-walkable point if it is boxed in on every
 * side. See the note at the `play:interactRequested` handler for why the blind
 * ring search alone is not enough.
 */
function standingSpotFor(grid: Grid, p: Placement, raw: Vec3): Vec3 {
  const base = grid.baseCells(p.anchor, p.footprint, p.rot)
  const own = new Set(base.map((c) => `${c.gx},${c.gz}`))
  let best: Vec3 | null = null
  let bestD = Infinity
  const consider = (c: Cell): void => {
    if (own.has(`${c.gx},${c.gz}`) || !grid.inBounds(c) || !grid.isWalkable(c.gx, c.gz)) return
    const w = grid.cellToWorld(c)
    const d = Math.hypot(w.x - walker.x, w.z - walker.z)
    if (d < bestD) {
      bestD = d
      best = w
    }
  }
  for (const c of base) {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dz === 0) continue
        consider({ gx: c.gx + dx, gy: 0, gz: c.gz + dz })
      }
    }
  }
  if (best !== null) return best
  return nearestWalkable(grid, raw.x, raw.z)
}

function finishArrival(): void {
  const sceneId = world.activeSceneId
  if (sceneId === null) return
  bus.emit('play:arrived', { sceneId, at: { x: walker.x, y: 0, z: walker.z } })

  const id = pendingInteract
  pendingInteract = null
  if (id === null) return

  const node = world.require(sceneId)
  const placement = node.grid.placement(id)
  if (placement === null) return

  // Turn to face the thing before reacting to it. Reads as attention.
  const centre = node.grid.footprintCenter(placement.anchor, placement.footprint, placement.rot)
  walker.faceTowards(centre.x, centre.z)

  bus.emit('play:interacted', { instanceId: id, defId: placement.defId, sceneId })
  const def = requirePlaceable(placement.defId)
  bus.emit('audio:cue', { cue: `interact:${def.category}` })
  save.request('interact')
}

// ---------------------------------------------------------------------------
// art
// ---------------------------------------------------------------------------

/** How big the monster is drawn right now: room, town, or creator stage. */
function monsterScale(): number {
  if (app.phase === 'monsterCreator') return CREATOR_SCALE
  const id = world.activeSceneId
  if (id === null) return EXTERIOR_SCALE
  return world.require(id).def.kind === 'interior' ? INTERIOR_SCALE : EXTERIOR_SCALE
}

function rebuild(): void {
  const sceneId = world.activeSceneId
  if (sceneId === null) return
  const node = world.require(sceneId)
  const carried = rig === null ? null : (rig.carrySlot.children[0] ?? null)

  if (rig !== null) rig.root.parent?.remove(rig.root)
  builtScale = monsterScale()
  rig = buildMonster(app.monster, { detail: 'full', scale: builtScale })
  animator = new MonsterAnimator(rig, engine.rng)
  rig.root.position.set(walker.x, 0, walker.z)
  rig.root.rotation.y = walker.facing
  if (carried !== null) rig.carrySlot.add(carried)
  node.actors.add(rig.root)
}

// ---------------------------------------------------------------------------
// public surface (monster crew + creator)
// ---------------------------------------------------------------------------

/** Where the player is standing, for crews that need it without the bus. */
export function playerPosition(): Vec3 {
  return { x: walker.x, y: 0, z: walker.z }
}

export function teleportPlayer(p: Vec3): void {
  walker.setPosition(p.x, p.z)
  pendingInteract = null
  // Placed, not turned. Easing from a stale yaw would spin the monster on every
  // door it walks through and on every harness teleport.
  shownFacing = walker.facing
  if (rig !== null) {
    rig.root.position.set(p.x, 0, p.z)
    rig.root.rotation.y = shownFacing
  }
}

/** Play an action on the kid's monster directly (creator preview, NPC greets). */
export function playerAction(action: string, seconds?: number): void {
  animator?.play(action, seconds)
}

export function playerRig(): MonsterRig | null {
  return rig
}

/** Apply a creator choice. Rebuilds the art and autosaves. */
export function setPlayerLook(look: MonsterConfig, reason = 'creator'): void {
  app.monster = { ...look }
  rebuild()
  save.request(`monster:${reason}`)
}

export function playerLookCreated(): boolean {
  return created
}

/** Stop the monster where it stands (creator, cut-scenes, scene changes). */
export function haltPlayer(): void {
  walker.stop()
  pendingInteract = null
}

/** Hats/outfits the kid owns. The creator only offers these. */
export function wardrobe(): string[] {
  return [...owned]
}

export function markPlayerCreated(): void {
  if (created) return
  created = true
  save.request('monster:created')
}

/**
 * Hand something to the monster to carry (a plate, a gift, an ice cream). Pass
 * null to empty its hands. The venue crew drives this through `spawn:request`.
 */
export function setCarried(obj: THREE.Object3D | null): void {
  if (rig === null) return
  rig.carrySlot.clear()
  if (obj !== null) {
    rig.carrySlot.add(obj)
    animator?.play('carry', 0)
  } else if (animator?.currentAction === 'carry') {
    animator.stop()
  }
}
